import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button, Metric } from "@odingard/design-system";
import { ErrorState, LoadingState } from "@odingard/design-system";
import type { CatalogApplication, CatalogCustomerState, CatalogPreference } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { hasMeetingEntitlement } from "../../components/MeetingGuard";

// Days remaining until an ISO timestamp, or null when there's no future date.
function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

// Order the launcher: pinned apps first, then most-recently-opened, then the
// catalog's default order. The catalog order (already sorted by the API) is the
// stable tie-breaker so unpinned/never-opened apps keep their canonical layout.
function orderedApps(apps: CatalogApplication[], prefs: CatalogPreference[]): CatalogApplication[] {
  const bySlug = new Map(prefs.map((p) => [p.slug, p]));
  const rank = new Map(apps.map((a, i) => [a.slug, i]));
  return [...apps].sort((a, b) => {
    const pa = bySlug.get(a.slug);
    const pb = bySlug.get(b.slug);
    const pinnedDiff = Number(pb?.pinned ?? false) - Number(pa?.pinned ?? false);
    if (pinnedDiff !== 0) return pinnedDiff;
    const ta = pa?.lastOpenedAt ? Date.parse(pa.lastOpenedAt) : 0;
    const tb = pb?.lastOpenedAt ? Date.parse(pb.lastOpenedAt) : 0;
    if (tb !== ta) return tb - ta;
    return (rank.get(a.slug) ?? 0) - (rank.get(b.slug) ?? 0);
  });
}

export function StudioHome() {
  const { activeOrg, user } = useAuth();
  const navigate = useNavigate();
  const orgId = activeOrg?.id ?? "";
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Fully catalog-driven: metadata (icon/name/route) from the catalog, and the
  // server-authoritative per-org customer state (entitlement + lifecycle) from
  // the resolver. The browser never decides launchability.
  const state = useAsync(
    async () => {
      const [apps, states, prefs] = await Promise.all([
        api.catalogApplications({ delivery: "platform", pageSize: 100 }),
        api.catalogState(orgId),
        api.catalogPreferences(orgId),
      ]);
      const platformApps = apps.applications.filter((a) => a.delivery === "platform");
      const meetingEntitled = platformApps.some((app) => app.slug === "app-meeting")
        ? hasMeetingEntitlement((await api.entitlements(orgId)).entitlements)
        : false;
      return {
        apps: platformApps,
        states: states.states,
        preferences: prefs.preferences,
        meetingEntitled,
      };
    },
    [orgId],
  );

  async function startTrial(slug: string) {
    setBusyKey(slug);
    setNotice(null);
    try {
      const res = await api.startAppTrial(orgId, slug);
      setNotice(res.reused ? "Trial already active for this app." : "Free trial started. Enjoy 14 days.");
      await state.reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not start the trial.");
    } finally {
      setBusyKey(null);
    }
  }

  async function togglePin(slug: string, pinned: boolean) {
    setBusyKey(`pin:${slug}`);
    try {
      if (pinned) await api.unpinCatalogApp(orgId, slug);
      else await api.pinCatalogApp(orgId, slug);
      await state.reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not update the pin.");
    } finally {
      setBusyKey(null);
    }
  }

  // Record the launch (advisory telemetry) then navigate. A failed record must
  // never block launching, so navigation happens regardless.
  async function launch(slug: string, to: string) {
    void api.recordCatalogOpen(orgId, slug).catch(() => undefined);
    navigate(to);
  }

  function renderCard(app: CatalogApplication, cs: CatalogCustomerState | undefined, pref: CatalogPreference | undefined, meetingEntitled: boolean) {
    const st = cs?.state ?? "coming_soon";
    const launchableMeeting = app.slug === "app-meeting" && meetingEntitled;
    const comingSoon = st === "coming_soon" && !launchableMeeting;
    const onTrial = st === "trialing";
    const expired = st === "expired";
    const canLaunch = (cs?.canLaunch ?? false) || launchableMeeting;
    const canTrial = cs?.canTrial ?? false;
    const canSubscribe = cs?.canSubscribe ?? false;
    const daysLeft = onTrial ? daysUntil(cs?.trialEndsAt ?? null) : null;
    const launchTo = cs?.route ?? app.route ?? "/studio";
    const pinned = pref?.pinned ?? false;
    // Pins only make sense for apps the member can actually open.
    const pinnable = canLaunch || pinned;

    return (
      <div className={`launch-card ${comingSoon ? "launch-card--soon" : ""}`} key={app.id}>
        <div className="ods-row" style={{ justifyContent: "space-between" }}>
          <div className="launch-card__icon" aria-hidden="true">
            {app.icon}
          </div>
          <div className="ods-row" style={{ gap: 8 }}>
            {pinnable && (
              <button
                type="button"
                className="launch-card__pin"
                aria-pressed={pinned}
                aria-label={pinned ? `Unpin ${app.name}` : `Pin ${app.name}`}
                title={pinned ? "Unpin" : "Pin"}
                disabled={busyKey === `pin:${app.slug}`}
                onClick={() => togglePin(app.slug, pinned)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1rem", opacity: pinned ? 1 : 0.4 }}
              >
                {pinned ? "★" : "☆"}
              </button>
            )}
            {comingSoon ? (
              <Badge tone="neutral">Coming soon</Badge>
            ) : onTrial ? (
              <Badge tone="info">{daysLeft != null ? `Trial · ${daysLeft}d left` : "Trial"}</Badge>
            ) : expired ? (
              <Badge tone="danger">Trial expired</Badge>
            ) : st === "subscribed" ? (
              <Badge tone="success">Active</Badge>
            ) : st === "contact_sales" ? (
              <Badge tone="neutral">Contact sales</Badge>
            ) : (
              <Badge tone="warning">Not enabled</Badge>
            )}
          </div>
        </div>
        <div>
          <h3 style={{ margin: "0 0 6px" }}>{app.name}</h3>
          <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", margin: 0 }}>{app.tagline || app.description}</p>
          {cs?.notice && <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", margin: "6px 0 0" }}>{cs.notice}</p>}
        </div>
        <div className="ods-row">
          {comingSoon ? (
            <Button variant="ghost" size="sm" disabled>
              Coming soon
            </Button>
          ) : canLaunch ? (
            <Button size="sm" onClick={() => launch(app.slug, launchTo)}>
              Launch
            </Button>
          ) : expired ? (
            <Link to="/pricing">
              <Button size="sm" variant="secondary">
                Upgrade
              </Button>
            </Link>
          ) : (
            <>
              {canTrial && (
                <Button size="sm" disabled={busyKey === app.slug} onClick={() => startTrial(app.slug)}>
                  {busyKey === app.slug ? "Starting…" : "Start free trial"}
                </Button>
              )}
              {canSubscribe && (
                <Link to="/pricing">
                  <Button size="sm" variant={canTrial ? "ghost" : "secondary"}>
                    Upgrade
                  </Button>
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Welcome back, {user?.displayName?.split(" ")[0] ?? "there"}</h1>
          <p>{activeOrg?.name} · your applications and workspace.</p>
        </div>
      </div>

      {notice && <div className="banner banner--info">{notice}</div>}
      {state.loading && <LoadingState label="Loading your applications…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data && (
        <div className="launcher-grid">
          {orderedApps(state.data.apps, state.data.preferences).map((app) =>
            renderCard(
              app,
              state.data!.states.find((s) => s.slug === app.slug),
              state.data!.preferences.find((p) => p.slug === app.slug),
              state.data!.meetingEntitled,
            ),
          )}
        </div>
      )}

      <div className="section" style={{ paddingBottom: 0 }}>
        <h2 style={{ fontSize: "var(--ods-text-lg)" }}>Your organization at a glance</h2>
        <div className="metric-grid">
          <div className="metric-card">
            <Metric value={activeOrg?.role ?? "None"} label="Your role" />
          </div>
          <div className="metric-card">
            <Metric value={state.data?.states.filter((s) => s.canLaunch).length ?? 0} label="Launchable apps" />
          </div>
          <div className="metric-card">
            <Metric value={state.data?.apps.filter((a) => a.lifecycleStatus === "available").length ?? 0} label="Available apps" />
          </div>
          <div className="metric-card">
            <Metric value={activeOrg?.permissions.length ?? 0} label="Your permissions" />
          </div>
        </div>
      </div>
    </>
  );
}
